import { useState, useEffect, useRef, useCallback, FormEvent } from "react";
import { useQuery } from "@tanstack/react-query";
import { Wrench, AlertTriangle, CheckCircle, Bell } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { CountdownTimer } from "@/modules/maintenance/components/CountdownTimer";
import { maintenanceWindowApi } from "@/services/maintenanceWindowApi";
import { useAuth } from "@/contexts/AuthContext";
import { cn } from "@/lib/utils";
import type { MaintenanceStateResponse } from "@/types/maintenance-window";

function calcProgress(startedAt: string | undefined, estimatedEnd: string | undefined): number {
  if (!startedAt || !estimatedEnd) return 0;
  const start = new Date(startedAt).getTime();
  const end = new Date(estimatedEnd).getTime();
  const now = Date.now();
  const total = end - start;
  if (total <= 0) return 100;
  const elapsed = now - start;
  return Math.min(100, Math.max(0, (elapsed / total) * 100));
}

function formatRemainingTime(estimatedEnd: string | undefined): string {
  if (!estimatedEnd) return "";
  const remaining = Math.max(0, new Date(estimatedEnd).getTime() - Date.now());
  const mins = Math.ceil(remaining / 60_000);
  if (mins <= 0) return "Finalizando...";
  return `~${mins} min restantes`;
}

export const MaintenanceFullscreen: React.FC = () => {
  const { login } = useAuth();
  const [showLogin, setShowLogin] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  const [failedAttempts, setFailedAttempts] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const clickCountRef = useRef(0);
  const clickTimerRef = useRef<ReturnType<typeof setTimeout>>();

  // Story 4.3: Opt-in "Me Avise"
  const [optinEmail, setOptinEmail] = useState("");
  const [optinSubmitted, setOptinSubmitted] = useState(() => {
    return localStorage.getItem("seweb:maintenance:optinDone") === "true";
  });
  const [optinSubmitting, setOptinSubmitting] = useState(false);
  const [optinError, setOptinError] = useState("");

  const autoSaved = localStorage.getItem("seweb:maintenance:autoSaved") === "true";

  const { data, dataUpdatedAt } = useQuery<MaintenanceStateResponse>({
    queryKey: ["maintenance-status-fullscreen"],
    queryFn: maintenanceWindowApi.getStatus,
    refetchInterval: 5_000,
    refetchIntervalInBackground: true,
  });

  // AC3: Redirect when maintenance ends
  useEffect(() => {
    if (data && !data.is_active) {
      window.location.href = "/";
    }
  }, [data]);

  // AC4: Ctrl+Shift+M keyboard shortcut
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.shiftKey && e.key === "M") {
        e.preventDefault();
        setShowLogin(true);
        setLoginError("");
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  // AC4: Triple-click on wrench icon
  const handleIconClick = useCallback(() => {
    clickCountRef.current += 1;

    if (clickTimerRef.current) {
      clearTimeout(clickTimerRef.current);
    }

    if (clickCountRef.current >= 3) {
      clickCountRef.current = 0;
      setShowLogin(true);
      setLoginError("");
      return;
    }

    clickTimerRef.current = setTimeout(() => {
      clickCountRef.current = 0;
    }, 500);
  }, []);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (clickTimerRef.current) clearTimeout(clickTimerRef.current);
    };
  }, []);

  // AC5: Login submit
  const handleLogin = async (e: FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;

    setIsSubmitting(true);
    setLoginError("");

    try {
      await login(email, password);

      // Redirect to admin maintenance panel
      window.location.href = "/admin/maintenance";
    } catch (err: unknown) {
      const newFailedCount = failedAttempts + 1;
      setFailedAttempts(newFailedCount);

      const msg = err instanceof Error ? err.message : "Erro de conexão. Tente novamente.";
      setLoginError(msg);

      // AC6: Hide after 3 failed attempts
      if (newFailedCount >= 3) {
        setShowLogin(false);
        setFailedAttempts(0);
        setEmail("");
        setPassword("");
        setLoginError("");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Story 4.3: Handle opt-in submit
  const handleOptinSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (optinSubmitting || optinSubmitted) return;

    setOptinSubmitting(true);
    setOptinError("");
    try {
      await maintenanceWindowApi.submitReturnOptIn(optinEmail);
      setOptinSubmitted(true);
      localStorage.setItem("seweb:maintenance:optinDone", "true");
    } catch (err) {
      console.error("[MaintenanceFullscreen] opt-in failed:", err);
      const msg =
        err instanceof Error ? err.message : "Não foi possível registrar. Tente novamente.";
      setOptinError(msg);
    } finally {
      setOptinSubmitting(false);
    }
  };

  // Stale data indicator — re-renders every 5s to keep "há Xs" current
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 5_000);
    return () => clearInterval(id);
  }, []);

  const staleness = dataUpdatedAt ? (Date.now() - dataUpdatedAt) / 1000 : 0;
  const isStale = staleness > 30;
  const isVeryStale = staleness > 60;

  const estimatedEnd = data?.estimated_end ? new Date(data.estimated_end) : null;

  const modules = data?.modules ?? [];
  const isPartial = data?.scope === "partial" && modules.length > 0;

  const progress = calcProgress(data?.started_at, data?.estimated_end);
  const remainingLabel = formatRemainingTime(data?.estimated_end);

  return (
    <main className="flex items-center justify-center min-h-screen bg-background p-4">
      <Card className="w-full max-w-lg text-center space-y-6">
        <CardContent className="pt-8 pb-8 space-y-6">
          {/* Wrench icon */}
          <div
            data-testid="maintenance-icon"
            onClick={handleIconClick}
            className="flex justify-center cursor-default select-none"
          >
            <Wrench
              className={cn(
                "text-muted-foreground",
                "w-12 h-12 sm:w-16 sm:h-16",
                "motion-safe:animate-[spin_8s_ease-in-out_infinite]"
              )}
              aria-hidden="true"
            />
          </div>

          {/* Title */}
          <h1 className="text-2xl sm:text-3xl font-semibold text-foreground">
            Sistema em Manutenção
          </h1>

          {/* Countdown */}
          {estimatedEnd && (
            <CountdownTimer
              targetTime={estimatedEnd}
              size="xl"
              format="human"
              className="text-3xl sm:text-5xl block"
            />
          )}

          {/* Progress bar */}
          {data?.estimated_end && (
            <div className="space-y-2 px-4">
              <Progress value={progress} className="h-2" />
              <p className="text-sm text-muted-foreground">{remainingLabel}</p>
            </div>
          )}

          {/* Reason */}
          {data?.message && <p className="text-sm text-muted-foreground px-4">{data.message}</p>}

          {/* Affected modules */}
          <p className="text-sm text-muted-foreground">
            {isPartial ? `Módulos afetados: ${modules.join(", ")}` : "Todos os serviços"}
          </p>

          {/* Auto-saved badge */}
          {autoSaved && (
            <Badge
              variant="outline"
              className="border-green-500 text-green-600 dark:text-green-400 gap-1"
            >
              <CheckCircle className="w-3.5 h-3.5" />
              Seu trabalho foi salvo automaticamente
            </Badge>
          )}

          {/* Story 4.3: Opt-in "Me Avise quando voltar" */}
          <div className="space-y-2 px-4">
            {optinSubmitted ? (
              <p className="text-sm text-green-600 dark:text-green-400 flex items-center justify-center gap-1">
                <Bell className="w-4 h-4" />
                Você será notificado quando o sistema voltar
              </p>
            ) : (
              <form onSubmit={handleOptinSubmit} className="space-y-2">
                <p className="text-sm text-muted-foreground">Me avise quando o sistema voltar:</p>
                <div className="flex gap-2">
                  <Input
                    type="email"
                    placeholder="seu@email.com"
                    value={optinEmail}
                    onChange={(e) => setOptinEmail(e.target.value)}
                    required
                    autoComplete="email"
                    className="flex-1"
                  />
                  <Button type="submit" variant="outline" size="sm" disabled={optinSubmitting}>
                    {optinSubmitting ? "..." : "Avisar"}
                  </Button>
                </div>
                {optinError && <p className="text-xs text-destructive">{optinError}</p>}
              </form>
            )}
          </div>

          {/* Stale data indicator */}
          {isStale && (
            <p className="text-xs text-muted-foreground flex items-center justify-center gap-1">
              {isVeryStale && <AlertTriangle className="w-3.5 h-3.5 text-yellow-500" />}
              Última atualização há {Math.floor(staleness)}s
            </p>
          )}

          {/* Hidden login form */}
          {showLogin && (
            <form onSubmit={handleLogin} className="space-y-3 px-4 animate-in fade-in duration-300">
              <Input
                type="email"
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
              />
              <Input
                type="password"
                placeholder="Senha"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
              />
              {loginError && <p className="text-sm text-destructive">{loginError}</p>}
              <Button type="submit" className="w-full" disabled={isSubmitting}>
                Entrar
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </main>
  );
};
